// Dashboard de Engagement - Wrapper con routing
// Soporta propiedades activas y archivadas

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { ArrowLeft, Inbox, Eye, RefreshCw } from 'lucide-react';
import { EngagementTab } from './components/admin/Engagement/EngagementTab';
import { getPropertyById } from './services/supabaseService';
import { getPropertyEngagementWithProfiles } from './services/supabase/engagement';

/**
 * Dashboard de Engagement
 * Ruta: /admin/engagement/:propertyId
 */
const EngagementDashboard = ({ properties }) => {
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const fromArchived = searchParams.get('from') === 'archived';

  // Obtener token de la URL
  const tokenFromUrl = searchParams.get('token');

  const [currentProperty, setCurrentProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estado de engagement data
  const [buyerSummaries, setBuyerSummaries] = useState([]);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  // ============================================
  // EFECTO: Cargar propiedad
  // ============================================
  useEffect(() => {
    const loadProperty = async () => {
      if (!propertyId) {
        navigate('/admin', { replace: true });
        return;
      }

      setLoading(true);
      setError(null);

      // Primero buscar en properties locales (activas)
      const localProperty = properties?.find(p => p.id === propertyId);
      
      if (localProperty) {
        setCurrentProperty(localProperty);
        setLoading(false);
        return;
      }

      // Si no está en locales, buscar en Supabase (puede ser archivada)
      try {
        const fetchedProperty = await getPropertyById(propertyId);
        if (fetchedProperty) {
          setCurrentProperty(fetchedProperty);
        } else {
          setError('Propiedad no encontrada');
        }
      } catch (err) {
        console.error('Error cargando propiedad:', err);
        setError('Error al cargar la propiedad');
      } finally {
        setLoading(false);
      }
    };

    loadProperty();
  }, [propertyId, properties, navigate]);

  // ============================================
  // EFECTO: Cargar datos de engagement
  // ============================================
  useEffect(() => {
    if (currentProperty?.id) {
      loadEngagementData();
    }
  }, [currentProperty?.id]);

  const loadEngagementData = async () => {
    if (!currentProperty?.id) return;
    setEngagementLoading(true);
    try {
      const summaries = await getPropertyEngagementWithProfiles(currentProperty.id);
      setBuyerSummaries(summaries);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error cargando engagement:', err);
    } finally {
      setEngagementLoading(false);
    }
  };

  // ============================================
  // HANDLER: Volver al admin
  // ============================================
  const handleBack = () => {
    // Guardar en sessionStorage si venimos de archivadas
    if (fromArchived) {
      sessionStorage.setItem('adminShowArchived', 'true');
    }
    navigate('/admin');
  };

  // ============================================
  // RENDER: Loading
  // ============================================
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Cargando analytics...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // VALIDACIÓN: Acceso por autenticación o token
  // ============================================
  const hasValidToken = tokenFromUrl && currentProperty?.spectatorToken === tokenFromUrl;
  const hasValidAccess = isAuthenticated || hasValidToken;
  const isSpectatorMode = !isAuthenticated && hasValidToken;

  if (!loading && currentProperty && !hasValidAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-red-500/30 text-center">
          <div className="bg-red-500/10 p-4 rounded-xl inline-block mb-4">
            <Inbox className="w-12 h-12 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Acceso denegado</h2>
          <p className="text-slate-400">
            No tienes permisos para ver esta página.
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Error o no encontrada
  // ============================================
  if (error || !currentProperty) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Inbox className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-400 mb-2">
            {error || 'Propiedad no encontrada'}
          </h3>
          <button
            onClick={handleBack}
            className="text-rose-400 hover:text-rose-300 transition-all"
          >
            ← Volver al Admin
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Dashboard completo
  // ============================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-[1800px] mx-auto p-6">
        {/* Header compacto: Título + Refresh centrado + Botones derecha */}
        <div className="flex items-center justify-between mb-4 bg-slate-800/50 rounded-xl px-6 py-3 border border-slate-700">
          <h1 className="text-xl font-bold text-white">
            📊 {currentProperty.title}
          </h1>

          {/* Centro: Actualizado + Refresh */}
          <div className="flex items-center gap-3">
            {lastRefresh && (
              <span className="text-xs text-slate-500">
                Actualizado: {lastRefresh.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <button
              onClick={loadEngagementData}
              disabled={engagementLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-xs transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${engagementLoading ? 'animate-spin' : ''}`} />
              {engagementLoading ? 'Cargando...' : 'Actualizar'}
            </button>
          </div>

          <div className="flex items-center gap-4">
            {/* Enlace a visión espectador */}
            {currentProperty?.spectatorToken && (
              <a
                href={`/property/${currentProperty.id}/spectate/${currentProperty.spectatorToken}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-all text-sm font-medium"
              >
                <Eye className="w-4 h-4" />
                Visión espectador
              </a>
            )}

            {/* Botón volver - solo para admin logueado */}
            {isAuthenticated && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-rose-400 hover:text-rose-300 transition-all text-sm font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver al Admin
              </button>
            )}
          </div>
        </div>

        {/* Renderizar EngagementTab */}
        <EngagementTab
          property={currentProperty}
          buyerSummaries={buyerSummaries}
          loading={engagementLoading}
          isSpectatorMode={isSpectatorMode}
        />
      </div>
    </div>
  );
};

export default EngagementDashboard;